/**
 * Email-code sign-in: the in-flight operation, its result, and a small
 * tab-scoped resume record, so an interrupted code step can be picked up
 * after the dialog closes or the tab reloads.
 *
 * Privacy: the record holds only the address, its state and a few times.
 * It never holds the code, a token, a password or provider text.
 *
 * Storage rule: tab-scoped sessionStorage under a `whw-` key, never `adia-*`
 * and never localStorage. The owner check treats `adia-*` localStorage keys
 * as protected user state (content-owner.ts), so this key must stay outside
 * that namespace. Same precedent as the update flow (app-update-core.ts).
 *
 * Operation rules:
 * - At most one current operation (send, resend or verify).
 * - Abandoning a send (address change) or seeing a signed-in session moves
 *   the generation on. A completion acts only while it is still the current
 *   operation of the current generation; any other completion does nothing.
 * - Nothing here sends, submits or calls auth by itself. Callers pass the
 *   provider call in.
 *
 * No React and no browser-only imports, so the Node probe can load it.
 */
import { mapEmailSendError, mapEmailVerifyError, type AuthCallResult } from './auth-errors';

export const EMAIL_SIGNIN_RESUME_KEY = 'whw-email-signin-v1';
/** Matches the configured 3600 s email code life. */
export const RESUME_WINDOW_MS = 60 * 60_000;
/** Allowed clock skew for an event time that looks slightly in the future. */
export const EVENT_SKEW_MS = 60_000;
/** Minimum gap between two code emails to one address. */
export const RESEND_INTERVAL_MS = 60_000;
/** A retry deadline further ahead than this is ignored. */
export const MAX_DEADLINE_AHEAD_MS = 60 * 60_000;
/** After this long in flight, the dialog says the request is slow. */
export const SLOW_REQUEST_MS = 15_000;

export type ResumeState = 'pending' | 'sent' | 'limited';

export interface ResumeRecord {
  v: 1;
  email: string;
  state: ResumeState;
  /** When the send started (pending), was accepted (sent) or refused (limited). */
  eventAt: number;
  resendAfter: number | null;
  verifyAfter: number | null;
}

export type OperationKind = 'send' | 'resend' | 'verify';

export interface EmailSigninOperation {
  id: number;
  kind: OperationKind;
  email: string;
  generation: number;
  startedAt: number;
}

export interface EmailSigninOutcome {
  opId: number;
  kind: OperationKind;
  email: string;
  ok: boolean;
  /** i18n key under auth.*; null when ok. */
  key: string | null;
  /**
   * A first send whose outcome is unknown: its `pending` record was kept, so
   * the dialog shows the "we may have sent" code step. Only on failures.
   */
  pending: boolean;
}

export interface EmailSigninSnapshot {
  current: EmailSigninOperation | null;
  outcome: EmailSigninOutcome | null;
  /** Bumps whenever the stored record changes. */
  recordVersion: number;
}

type Clock = () => number;
type StorageGetter = () => Storage | null;

const defaultStorage: StorageGetter = () => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
};
const defaultClock: Clock = () => Date.now();
const defaultOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

let getStorage: StorageGetter = defaultStorage;
let clock: Clock = defaultClock;
let isOnline: () => boolean = defaultOnline;

let generation = 0;
let nextId = 1;
let current: EmailSigninOperation | null = null;
let outcome: EmailSigninOutcome | null = null;
let recordVersion = 0;
let snapshot: EmailSigninSnapshot = { current, outcome, recordVersion };
const listeners = new Set<() => void>();

function publish(): void {
  snapshot = { current, outcome, recordVersion };
  for (const listener of [...listeners]) listener();
}

/** Test seam: replace storage, clock and connectivity, and reset all state. */
export function configureEmailSigninForTests(options: {
  storage?: StorageGetter;
  now?: Clock;
  online?: () => boolean;
} = {}): void {
  getStorage = options.storage ?? defaultStorage;
  clock = options.now ?? defaultClock;
  isOnline = options.online ?? defaultOnline;
  generation = 0;
  nextId = 1;
  current = null;
  outcome = null;
  recordVersion = 0;
  snapshot = { current, outcome, recordVersion };
  listeners.clear();
}

export function subscribeEmailSignin(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getEmailSigninSnapshot(): EmailSigninSnapshot {
  return snapshot;
}

export function emailSigninNow(): number {
  return clock();
}

// ---- Stored record ----

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function validDeadline(value: unknown, now: number): number | null {
  if (!isFiniteNumber(value)) return null;
  // A past deadline just means "available now"; one too far ahead is dropped.
  if (value > now + MAX_DEADLINE_AHEAD_MS) return null;
  return value;
}

/** Validate raw stored text. Returns null for anything unusable. */
export function parseResumeRecord(raw: string | null, now: number): ResumeRecord | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (row.v !== 1) return null;
  if (typeof row.email !== 'string') return null;
  const email = row.email.trim();
  if (!email || email.length > 320) return null;
  if (row.state !== 'pending' && row.state !== 'sent' && row.state !== 'limited') return null;
  // Event time: must be real, not ahead of now beyond skew, within the window.
  if (!isFiniteNumber(row.eventAt)) return null;
  if (row.eventAt > now + EVENT_SKEW_MS) return null;
  if (now - row.eventAt > RESUME_WINDOW_MS) return null;
  return {
    v: 1,
    email,
    state: row.state,
    eventAt: row.eventAt,
    resendAfter: validDeadline(row.resendAfter, now),
    verifyAfter: validDeadline(row.verifyAfter, now),
  };
}

function readRaw(): string | null {
  try {
    return getStorage()?.getItem(EMAIL_SIGNIN_RESUME_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Read the record without changing storage. */
export function peekResumeRecord(): ResumeRecord | null {
  return parseResumeRecord(readRaw(), clock());
}

/** Remove a stored value that no longer validates (expired, bad time, malformed). */
export function pruneResumeRecord(): void {
  const raw = readRaw();
  if (raw !== null && parseResumeRecord(raw, clock()) === null) removeRecord();
}

function writeRecord(record: ResumeRecord): void {
  try {
    getStorage()?.setItem(EMAIL_SIGNIN_RESUME_KEY, JSON.stringify(record));
  } catch {
    // Storage refused: resume is a convenience, sign-in still works.
  }
  recordVersion += 1;
}

function removeRecord(): void {
  try {
    getStorage()?.removeItem(EMAIL_SIGNIN_RESUME_KEY);
  } catch {
    // Nothing to do; an unreadable value is treated as absent anyway.
  }
  recordVersion += 1;
}

/** When the next code may be requested, from this record. Null = now. */
export function resendAvailableAt(record: ResumeRecord | null): number | null {
  if (!record) return null;
  if (record.state === 'limited') return record.resendAfter;
  return Math.max(record.eventAt + RESEND_INTERVAL_MS, record.resendAfter ?? 0);
}

// ---- Operations ----

/**
 * The auth service itself answered and refused (a 4xx response). That is the
 * only failure that proves no code went out. No response, any 5xx (gateway
 * 502/503/504 included) and a thrown call are uncertain: the request may have
 * reached the provider. Browser online status is never evidence either way.
 */
function refusedByProvider(result: AuthCallResult): boolean {
  return (
    result.retryable !== true &&
    typeof result.status === 'number' &&
    result.status >= 400 &&
    result.status < 500
  );
}

/**
 * The rate-limit guidance a `limited` record carries on the email step, so it
 * is shown again after the dialog reopens or the tab reloads: the "please
 * wait" message while a stated deadline runs, the "try again later" message
 * when the provider gave none, nothing once a stated deadline has passed.
 */
export function limitedGuidanceKey(record: ResumeRecord | null, now: number): string | null {
  if (!record || record.state !== 'limited') return null;
  if (record.resendAfter === null) return 'auth.errorSendRateLimited';
  return record.resendAfter > now ? 'auth.errorSendRateLimitedWait' : null;
}

function isCurrent(op: EmailSigninOperation): boolean {
  return current !== null && current.id === op.id && op.generation === generation;
}

function begin(kind: OperationKind, email: string): EmailSigninOperation | null {
  if (current) return null;
  current = { id: nextId++, kind, email, generation, startedAt: clock() };
  outcome = null;
  return current;
}

async function callSafely(
  call: () => Promise<AuthCallResult>,
): Promise<AuthCallResult> {
  try {
    return await call();
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'unknown' };
  }
}

/**
 * Request a code. `send` is used for a first request (and from a pending or
 * limited record), `resend` once a code was accepted. Returns false when
 * another operation is current.
 */
export function startEmailSend(
  kind: 'send' | 'resend',
  email: string,
  send: (email: string) => Promise<AuthCallResult>,
): boolean {
  const address = email.trim();
  if (!address) return false;
  const op = begin(kind, address);
  if (!op) return false;
  const previous = peekResumeRecord();
  const sameAddress = previous?.email === address ? previous : null;
  if (kind === 'send') {
    writeRecord({
      v: 1,
      email: address,
      state: 'pending',
      eventAt: op.startedAt,
      resendAfter: sameAddress?.resendAfter ?? null,
      verifyAfter: null,
    });
  }
  publish();
  void callSafely(() => send(address)).then((result) => finishSend(op, result));
  return true;
}

function finishSend(op: EmailSigninOperation, result: AuthCallResult): void {
  if (!isCurrent(op)) return;
  current = null;
  const now = clock();
  const record = peekResumeRecord();
  const mine = record?.email === op.email ? record : null;
  if (!result.error) {
    writeRecord({
      v: 1,
      email: op.email,
      state: 'sent',
      eventAt: now,
      resendAfter: Math.max(now + RESEND_INTERVAL_MS, mine?.resendAfter ?? 0),
      verifyAfter: null,
    });
    outcome = { opId: op.id, kind: op.kind, email: op.email, ok: true, key: null, pending: false };
    publish();
    return;
  }
  const mapped = mapEmailSendError(result, { online: isOnline() });
  let pending = false;
  const deadline = mapped.retryAfterSeconds !== null ? now + mapped.retryAfterSeconds * 1000 : null;
  if (op.kind === 'resend' && mine && mine.state === 'sent') {
    // A code was already accepted; only a stated deadline changes the record.
    if (deadline !== null) {
      writeRecord({ ...mine, resendAfter: Math.max(deadline, mine.resendAfter ?? 0) });
    }
  } else if (mapped.reason === 'rate-limited') {
    // Refused before any accepted send in this record: nothing was delivered
    // by this attempt, and no earlier delivery is claimed.
    writeRecord({
      v: 1,
      email: op.email,
      state: 'limited',
      eventAt: now,
      resendAfter: deadline,
      verifyAfter: null,
    });
  } else if (mine && mine.state === 'pending') {
    if (refusedByProvider(result)) {
      // Answered and refused: nothing went out from this attempt.
      removeRecord();
    } else {
      // Uncertain (no response, server or gateway failure, thrown call, a
      // dropped connection or a reload abort): keep the marker, so resume
      // says "we may have sent" and nothing claims the code was not sent.
      pending = true;
    }
  }
  outcome = { opId: op.id, kind: op.kind, email: op.email, ok: false, key: mapped.key, pending };
  publish();
}

/** Check a code. Returns false when another operation is current. */
export function startEmailVerify(
  email: string,
  code: string,
  verify: (email: string, code: string) => Promise<AuthCallResult>,
): boolean {
  const address = email.trim();
  if (!address || code.length !== 6) return false;
  const op = begin('verify', address);
  if (!op) return false;
  publish();
  void callSafely(() => verify(address, code)).then((result) => finishVerify(op, result));
  return true;
}

function finishVerify(op: EmailSigninOperation, result: AuthCallResult): void {
  if (!isCurrent(op)) return;
  current = null;
  if (!result.error) {
    removeRecord();
    outcome = { opId: op.id, kind: 'verify', email: op.email, ok: true, key: null, pending: false };
    publish();
    return;
  }
  const mapped = mapEmailVerifyError(result, { online: isOnline() });
  const record = peekResumeRecord();
  if (mapped.retryAfterSeconds !== null && record && record.email === op.email) {
    const deadline = clock() + mapped.retryAfterSeconds * 1000;
    writeRecord({ ...record, verifyAfter: Math.max(deadline, record.verifyAfter ?? 0) });
  }
  outcome = { opId: op.id, kind: 'verify', email: op.email, ok: false, key: mapped.key, pending: false };
  publish();
}

/**
 * Take the waiting outcome once. The dialog calls this when it mounts and
 * whenever the snapshot changes, so an outcome reached while it was closed
 * is shown exactly once.
 */
export function takeEmailSigninOutcome(): EmailSigninOutcome | null {
  const taken = outcome;
  if (!taken) return null;
  outcome = null;
  publish();
  return taken;
}

/**
 * The address changed while a send was current: stop treating that send as
 * the current attempt. Its request cannot be cancelled; its late reply is
 * ignored. A verify is never abandoned here (the dialog disables changes
 * while one is current).
 */
export function abandonEmailSend(): void {
  if (!current || current.kind === 'verify') return;
  const abandoned = current;
  generation += 1;
  current = null;
  outcome = null;
  const record = peekResumeRecord();
  if (record && record.state === 'pending' && record.email === abandoned.email) removeRecord();
  publish();
}

/** "Use a different email": forget the code step for this address. */
export function changeEmailSigninAddress(): void {
  if (current?.kind === 'verify') return;
  if (current) {
    generation += 1;
    current = null;
  }
  outcome = null;
  if (readRaw() !== null) removeRecord();
  publish();
}

/**
 * A signed-in session was observed (called from useAuth's existing session
 * callbacks; makes no auth or network call). Invalidates every current or
 * abandoned completion and any waiting outcome, and clears the record.
 */
export function noteAuthenticatedSession(): void {
  const hadState = current !== null || outcome !== null || readRaw() !== null;
  generation += 1;
  current = null;
  outcome = null;
  if (readRaw() !== null) removeRecord();
  if (hadState) publish();
}
